// Need to use the React-specific entry point to import createApi
import type {
  BadgeTemplatePreviewRequest,
  BadgeTemplatePreviewResponse,
  BadgeTemplateRequest,
  BadgeTemplateResponse,
  BadgeTemplateSimpleResponse,
  GetBadgeTemplateListRequest,
  GetListResponseOfBadgeTemplateSimpleResponse,
} from 'src/api/ProactApiClient';
import {badgeTemplateApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery, voidClientQuery} from '../clientQuery';

// Define a service using a base URL and expected endpoints.
// Note: BadgeTemplate endpoints are super-admin instance management — every
// call carries an explicit target instanceId (integration-token pattern), since the
// acting admin manages arbitrary instances, not their own current instance.
export const badgeTemplatesApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getBadgeTemplateList: builder.query<
      GetListResponseOfBadgeTemplateSimpleResponse,
      GetBadgeTemplateListRequest
    >({
      queryFn: clientQuery(request => badgeTemplateApiClient.list(request)),
      providesTags: ['BadgeTemplates'],
    }),
    getBadgeTemplate: builder.query<
      BadgeTemplateResponse,
      {id: number; instanceId: number}
    >({
      queryFn: clientQuery(({id, instanceId}) =>
        badgeTemplateApiClient.get(id, instanceId)
      ),
      providesTags: ['BadgeTemplates'],
    }),
    getBadgeTemplateVersionHistory: builder.query<
      BadgeTemplateSimpleResponse[],
      {id: number; instanceId: number}
    >({
      queryFn: clientQuery(({id, instanceId}) =>
        badgeTemplateApiClient.getVersionHistory(id, instanceId)
      ),
      providesTags: ['BadgeTemplates'],
    }),
    createBadgeTemplate: builder.mutation<
      BadgeTemplateResponse,
      BadgeTemplateRequest
    >({
      queryFn: clientQuery(model => badgeTemplateApiClient.create(model)),
      invalidatesTags: ['BadgeTemplates'],
    }),
    updateBadgeTemplate: builder.mutation<
      BadgeTemplateResponse,
      BadgeTemplateRequest
    >({
      queryFn: clientQuery(model => badgeTemplateApiClient.update(model)),
      invalidatesTags: ['BadgeTemplates'],
    }),
    deleteBadgeTemplate: builder.mutation<
      void,
      {id: number; instanceId: number}
    >({
      queryFn: voidClientQuery(({id, instanceId}) =>
        badgeTemplateApiClient.delete(id, instanceId)
      ),
      invalidatesTags: ['BadgeTemplates'],
    }),
    previewBadgeTemplate: builder.mutation<
      BadgeTemplatePreviewResponse,
      BadgeTemplatePreviewRequest
    >({
      queryFn: clientQuery(request => badgeTemplateApiClient.preview(request)),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetBadgeTemplateListQuery,
  useGetBadgeTemplateQuery,
  useLazyGetBadgeTemplateQuery,
  useGetBadgeTemplateVersionHistoryQuery,
  useCreateBadgeTemplateMutation,
  useUpdateBadgeTemplateMutation,
  useDeleteBadgeTemplateMutation,
  usePreviewBadgeTemplateMutation,
} = badgeTemplatesApi;
